import * as React from 'react'

import { Box, Button, List, Text } from '@factorialco/deprecated-design-system'
import { GetEmployeesRoleForReviewDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { usePerformanceModuleContext } from 'modules/performance/hooks/usePerfomanceModuleContext'
import { useReviewees } from 'modules/reviews/hooks/useReviewees'
import { getTimeInUserTimeFormat } from 'modules/reviews/lib/getTimeInUserTimeFormat'
import type Review from 'modules/reviews/store/models/v2/Review'

import type { ReviewFactorialFormSchema } from '../ReviewSettings/CycleConfiguration/CycleConfiguration'
import { frequencyOptionLabel } from '../ReviewSettings/CycleConfiguration/CycleOptions'

interface reviewSettingsOption {
  label: string
  selection: string | boolean | undefined
  isToggle?: boolean
  tooltip?: string
}

interface StartReviewModalProps {
  review: Review
  onClose: () => void
  reviewData: ReviewFactorialFormSchema
}

const StartReviewModal = ({ review, onClose, reviewData }: StartReviewModalProps) => {
  const { performanceFeatureAvailability } = usePerformanceModuleContext()
  const trackOnClose = () => {
    track(trackingEvents.reviews.startReviewCancel)
    onClose()
  }

  const { loading: loadingReviewees, reviewees } = useReviewees(review)

  const revieweesIds = new Set(Object.keys(reviewees))

  const { data } = useQuery(GetEmployeesRoleForReviewDocument, {
    variables: { accessIds: [...revieweesIds].map((x) => Number(x)) },
    skip: !performanceFeatureAvailability.canSeeCompetencies,
  })

  const employees = data?.employees.employees

  const employeesWithoutRoleCompetencies = employees?.filter(
    (e) => !e.referenceContract?.jobCatalogLevel?.role?.competencies?.length
  )

  const {
    running,
    performAction: startReview,
    success,
    value,
  } = useAsyncAction(async (closePortal: ClosePortalFn) => {
    await review.startReview()

    track(trackingEvents.reviews.startReviewSubmit, {
      is_successful: success,
      error_code: !success && JSON.stringify(value),
    })

    review.fetch()

    closePortal()
  })

  const frequencyOption = review.hasSchedule
    ? frequencyOptionLabel().get(reviewData.frequency)
    : i18n.t('surveys.form.periodicity.no_periodicity')

  const startDate = i18n.t('surveys.review.settings.start.new_modal.start_date_selection', {
    start_on: reviewData.start_on,
    start_at: getTimeInUserTimeFormat(reviewData.scheduled_time),
  })

  const reviewSettings: reviewSettingsOption[] = [
    {
      label: i18n.t('surveys.review.settings.start.new_modal.start_date'),
      selection: startDate,
    },
    {
      label: i18n.t('surveys.review.settings.start.new_modal.frequency'),
      selection: frequencyOption,
    },
    {
      label: i18n.t('surveys.form.basic_info.form.labels.anonymous_reviews'),
      selection: review.isAnonymous,
      isToggle: true,
      tooltip: i18n.t('surveys.review.settings.start.new_modal.tooltip'),
    },
    {
      label: i18n.t(
        'surveys.form.answers_availability.form.labels.answers_available_for_managers_and_team_leads'
      ),
      selection: review.isVisibleToManagers,
      isToggle: true,
    },
  ]

  if (review.showScoreToggle) {
    reviewSettings.push({
      label: i18n.t('surveys.review.questions.employee_metrics_toggle.title'),
      selection: review.hasScoreQuestions,
      isToggle: true,
    } as reviewSettingsOption)
  }

  if (performanceFeatureAvailability.canSeeCompetencies) {
    reviewSettings.push({
      label: i18n.t('surveys.review.competencies.title'),
      selection: review.assessCompetencies,
      isToggle: true,
      tooltip: employeesWithoutRoleCompetencies?.length
        ? i18n.t('surveys.review.settings.start.new_modal.competency_tooltip', {
            without_competency: employeesWithoutRoleCompetencies?.length ?? 0,
            total: employees?.length ?? 0,
          })
        : '',
    } as reviewSettingsOption)
  }

  return (
    <Modal
      illustration="alert"
      title={i18n.t('surveys.review.settings.start.new_modal.title')}
      description={i18n.t('surveys.review.settings.start.new_modal.description')}
      onClose={trackOnClose}
      onSubmit={startReview}
      footer={
        <>
          <Button type="secondary" onClick={() => trackOnClose()}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={running} submit>
            {i18n.t('surveys.review.settings.start.new_modal.action')}
          </Button>
        </>
      }
    >
      <Box width="full">
        <List>
          {reviewSettings.map((setting) => (
            <List.Item key={setting.label}>
              <Box
                width="full"
                flexDirection="row"
                justifyContent="spaceBetween"
                alignItems="flexEnd"
                gap="s16"
              >
                <List.Title>
                  <Box flexDirection="row" alignItems="center" gap="s16">
                    <Text.Normal color="grey1000">{setting.label}</Text.Normal>
                    {setting.tooltip && (
                      <InformationTooltip information={setting.tooltip} icon="question" />
                    )}
                  </Box>
                </List.Title>
                <Box alignItems="center" justifyContent="flexEnd" alignSelf="center">
                  {setting.selection && (
                    <Text.Normal weight="medium" color="grey1000">
                      {setting.selection}
                    </Text.Normal>
                  )}
                  {setting.isToggle && (
                    <Text.Normal
                      color={setting.selection ? 'viridian1200' : 'radicalRed1000'}
                      weight="medium"
                    >
                      {setting.selection
                        ? i18n.t('common.toggled.enabled')
                        : i18n.t('common.toggled.disabled')}
                    </Text.Normal>
                  )}
                </Box>
              </Box>
            </List.Item>
          ))}
        </List>
      </Box>
    </Modal>
  )
}

export default observer(StartReviewModal)
